import strings from "global/constants/StringConstants";
import notifiers from "global/constants/NotificationConstants";
import { fillCopy, isNetworkError } from "helpers/methods";
import { formatShortDate } from "screens/ContractSearch/ContractSearchHelper";
import { ContractDocument } from "screens/ContractSearch/ContractSearchTypes";
import { apiStatus, getApiStatus } from "utils/apiStatus";
import {
  isFeatureBlockedError,
  isPermissionBlockedError,
} from "utils/apiFeatureGate";
import { parseAdminDisabled, parsePlanForbidden } from "utils/featureGate";
import {
  CANCEL_GRACE_MS,
  CARDS_PER_PAGE,
  COMPARE_PARTS,
  ChangeCardModel,
  CompareChangeType,
  CompareCounts,
  CompareDocument,
  CompareFailure,
  CompareFilterChip,
  CompareFilterKey,
  CompareNotice,
  CompareQuote,
  CompareResult,
  CompareSegment,
  CompareSide,
  CompareStatus,
  ComparePart,
  DEFAULT_RETRY_AFTER_SECONDS,
  LIMIT_FRAME_THRESHOLD_SECONDS,
  OldVersion,
  hasAllowedExtension,
  isolateName,
  safeFileName,
} from "./ContractCompareTypesAndValidation";

const copy = strings.contractCompare;

type Body = Record<string, unknown>;

// --------------------------------------------------------------------------------------- small formatters

/** "1 second" / "5 seconds". */
export const formatSeconds = (seconds: number): string =>
  seconds === 1
    ? copy.secondOne
    : fillCopy(copy.secondMany, { count: seconds });

/** The words for a busy / still-finishing frame at `secondsLeft` on its countdown. */
export const countdownMessage = (
  failure: Extract<CompareFailure, { kind: "busy" }>,
  secondsLeft: number,
): string =>
  fillCopy(failure.stillFinishing ? copy.stillFinishing : copy.busy, {
    time: formatSeconds(Math.max(0, secondsLeft)),
  });

/** "old version" / "new version". */
const versionLabel = (document: CompareDocument): string =>
  document === "original" ? copy.oldVersion : copy.newVersion;

/** Page numbers as the user reads them: sorted, no duplicates, runs joined ("2–4"), "a, b and c". */
const pageRanges = (pages: number[]): string => {
  const sorted = Array.from(new Set(pages)).sort((a, b) => a - b);
  const groups: string[] = [];
  for (let i = 0; i < sorted.length; ) {
    let j = i;
    while (j + 1 < sorted.length && sorted[j + 1] === sorted[j] + 1) j += 1;
    groups.push(j > i ? `${sorted[i]}–${sorted[j]}` : String(sorted[i]));
    i = j + 1;
  }
  if (groups.length <= 1) return groups[0] ?? "";
  return `${groups.slice(0, -1).join(", ")}${copy.listAnd}${groups[groups.length - 1]}`;
};

/** "page 4" / "pages 2–3 and 7". */
const pagesLabel = (pages: number[]): string =>
  fillCopy(pages.length === 1 ? copy.pageOne : copy.pageMany, {
    pages: pageRanges(pages),
  });

// ------------------------------------------------------------------------------------------ the answer

const isObject = (value: unknown): value is Body =>
  !!value && typeof value === "object" && !Array.isArray(value);

const asString = (value: unknown, fallback = ""): string =>
  typeof value === "string" ? value : fallback;

const asNumberOrNull = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const asCount = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) && value > 0
    ? Math.floor(value)
    : 0;

const TYPE_ORDER: CompareChangeType[] = [
  "changed",
  "added",
  "removed",
  "moved",
  "filled",
];

const TYPE_LABELS: Record<CompareChangeType, string> = {
  changed: copy.typeChanged,
  added: copy.typeAdded,
  removed: copy.typeRemoved,
  moved: copy.typeMoved,
  filled: copy.typeFilled,
};

const isChangeType = (value: unknown): value is CompareChangeType =>
  typeof value === "string" && (TYPE_ORDER as string[]).includes(value);

const toSide = (value: unknown): CompareSide | null => {
  if (!isObject(value)) return null;
  return {
    source: value.source === "library" ? "library" : "upload",
    documentId: typeof value.documentId === "string" ? value.documentId : null,
    fileName: safeFileName(asString(value.fileName)),
    kind: value.kind === "docx" ? "docx" : "pdf",
    pages: asNumberOrNull(value.pages),
  };
};

const toQuote = (value: unknown): CompareQuote | null => {
  if (!isObject(value)) return null;
  return {
    text: asString(value.text),
    page: asNumberOrNull(value.page),
    section:
      typeof value.section === "string" && value.section.trim()
        ? value.section
        : null,
    truncated: value.truncated === true,
  };
};

/** An op this app does not know is shown as unchanged text: dropping a piece would drop wording. */
const toSegments = (value: unknown): CompareSegment[] =>
  Array.isArray(value)
    ? value.filter(isObject).map((segment) => ({
        op: segment.op === "del" || segment.op === "ins" ? segment.op : "same",
        text: asString(segment.text),
      }))
    : [];

/** "p.1" for a PDF quote, its section ("paragraph 12") for Word, or nothing. */
const sideLocation = (quote: CompareQuote | null): string | null => {
  if (!quote) return null;
  if (quote.page != null) return fillCopy(copy.pageShort, { page: quote.page });
  return quote.section;
};

/** "old p.1 → new p.1", "new p.2", "old paragraph 12 → new p.2". */
const cardLocation = (
  before: CompareQuote | null,
  after: CompareQuote | null,
): string | null => {
  const parts: string[] = [];
  const old = sideLocation(before);
  const now = sideLocation(after);
  if (old) parts.push(fillCopy(copy.locationOld, { where: old }));
  if (now) parts.push(fillCopy(copy.locationNew, { where: now }));
  return parts.length ? parts.join(copy.locationArrow) : null;
};

const toCard = (value: unknown, index: number): ChangeCardModel | null => {
  if (!isObject(value)) return null;
  const type = isChangeType(value.type) ? value.type : "other";
  const before = toQuote(value.before);
  const after = toQuote(value.after);
  const heading =
    typeof value.section === "string" && value.section.trim()
      ? value.section
      : null;
  return {
    id: asString(value.id) || `change-${index}`,
    type,
    // A type this app does not know is still shown, under its own name, and only under "All".
    typeLabel:
      type === "other" ? asString(value.type, copy.typeOther) : TYPE_LABELS[type],
    heading,
    location: cardLocation(before, after),
    before,
    after,
    segments: toSegments(value.segments),
    sentence: asString(value.explanation),
    byCode: value.explainedBy !== "model",
  };
};

const toCounts = (value: unknown): CompareCounts => {
  const counts = isObject(value) ? value : {};
  return {
    changed: asCount(counts.changed),
    added: asCount(counts.added),
    removed: asCount(counts.removed),
    moved: asCount(counts.moved),
    filled: asCount(counts.filled),
  };
};

interface ToldWarning {
  code: string;
  document: CompareDocument | null;
  pages: number[] | null;
}

/** Warnings as the page reads them. A code is not checked here: an unknown one is ignored where they are used. */
const toWarnings = (value: unknown): ToldWarning[] =>
  Array.isArray(value)
    ? value.filter(isObject).map((warning) => {
        const pages = Array.isArray(warning.pages)
          ? warning.pages.filter(
              (page): page is number =>
                typeof page === "number" && Number.isInteger(page),
            )
          : [];
        return {
          code: asString(warning.code),
          document:
            warning.document === "original" || warning.document === "revised"
              ? warning.document
              : null,
          pages: pages.length ? pages : null,
        };
      })
    : [];

const subjectOf = (document: CompareDocument | null): string =>
  document === "original"
    ? copy.subjectOld
    : document === "revised"
      ? copy.subjectNew
      : copy.subjectFile;

/** "on page 4 of the new version" / "in the new version" / "on page 4" / "in the files". */
const whereOf = (
  document: CompareDocument | null,
  pages: number[] | null,
): string => {
  if (pages) {
    const label = pagesLabel(pages);
    return fillCopy(copy.whereOnPages, {
      pages: document
        ? fillCopy(copy.ofVersion, {
            pages: label,
            version: versionLabel(document),
          })
        : label,
    });
  }
  if (document) {
    return document === "original" ? copy.whereInOld : copy.whereInNew;
  }
  return copy.whereInFiles;
};

const noticeFor = (
  warning: ToldWarning,
  shown: number,
  total: number,
): string | null => {
  const { code, document, pages } = warning;
  switch (code) {
    case "images_differ":
      return fillCopy(copy.noticeImagesDiffer, {
        where: whereOf(document, pages),
      });
    case "ocr_text": {
      // No file named: "Some pages of the files are scans."
      const version = document ? versionLabel(document) : copy.versionBoth;
      if (!document || !pages) return fillCopy(copy.noticeOcrSome, { version });
      return fillCopy(
        pages.length === 1 ? copy.noticeOcrOne : copy.noticeOcrMany,
        { pages: pageRanges(pages), version },
      );
    }
    case "form_fields_not_compared":
      return fillCopy(copy.noticeFormFields, { Subject: subjectOf(document) });
    case "clauses_by_paragraph":
      return copy.noticeClausesByParagraph;
    case "tracked_changes":
      return fillCopy(copy.noticeTrackedChanges, {
        Subject: subjectOf(document),
      });
    case "hidden_characters":
      return copy.noticeHiddenCharacters;
    case "tables_not_compared":
      return copy.noticeTablesNotCompared;
    case "ai_unavailable":
      return copy.noticeSentencesByCode;
    case "changes_capped":
      return fillCopy(copy.noticeChangesCapped, { shown, total });
    // `explained_by_code` is on the card's tag; the two not-compared codes only matter when nothing was compared.
    default:
      return null;
  }
};

const buildNotices = (
  warnings: ToldWarning[],
  shown: number,
  total: number,
): CompareNotice[] =>
  warnings.flatMap((warning) => {
    const text = noticeFor(warning, shown, total);
    return text ? [{ code: warning.code, text }] : [];
  });

interface NotCompared {
  message: string;
  switchToUpload: boolean;
}

const uniqueDocuments = (
  warnings: ToldWarning[],
  code: string,
): CompareDocument[] => {
  const found: CompareDocument[] = [];
  warnings.forEach((warning) => {
    if (warning.code === code && warning.document && !found.includes(warning.document))
      found.push(warning.document);
  });
  return found;
};

/**
 * The words for a `not_compared` answer. A reason this app does not know, or one that
 * names no file where it must, shows the service's own summary rather than guessing.
 */
const describeNotCompared = (
  reason: string,
  summary: string,
  original: CompareSide | null,
  warnings: ToldWarning[],
): NotCompared => {
  const own: NotCompared = { message: summary, switchToUpload: false };
  switch (reason) {
    case "no_text_original":
    case "no_text_revised": {
      const document = reason === "no_text_original" ? "original" : "revised";
      return {
        message: fillCopy(copy.noText, { version: versionLabel(document) }),
        switchToUpload: false,
      };
    }
    case "too_many_scanned_pages": {
      const files = uniqueDocuments(warnings, "too_many_scanned_pages");
      if (files.length === 0) return own;
      return {
        message:
          files.length > 1
            ? copy.tooManyScannedBoth
            : fillCopy(copy.tooManyScanned, { version: versionLabel(files[0]) }),
        switchToUpload: false,
      };
    }
    case "unreadable_pages": {
      const entries = warnings.filter(
        (warning) => warning.code === "unreadable_pages" && warning.document,
      );
      if (entries.length === 0) return own;
      const parts = entries.map((entry) => {
        const version = versionLabel(entry.document as CompareDocument);
        return entry.pages
          ? fillCopy(copy.ofVersion, { pages: pagesLabel(entry.pages), version })
          : fillCopy(copy.somePagesOf, { version });
      });
      return {
        message: fillCopy(copy.unreadablePages, { parts: parts.join(copy.listAnd) }),
        switchToUpload: false,
      };
    }
    case "original_unavailable":
    case "original_too_large": {
      const name = original?.fileName ? isolateName(original.fileName) : copy.theOldVersion;
      return {
        message: fillCopy(
          reason === "original_unavailable"
            ? copy.originalUnavailable
            : copy.originalTooLarge,
          { name },
        ),
        switchToUpload: true,
      };
    }
    case "too_large":
      return { message: copy.tooLarge, switchToUpload: false };
    default:
      return own;
  }
};

const COMPARE_STATUSES: readonly CompareStatus[] = [
  "changes_found",
  "no_changes",
  "different_documents",
  "not_compared",
];

const isCompareStatus = (value: unknown): value is CompareStatus =>
  COMPARE_STATUSES.includes(value as CompareStatus);

/**
 * The service's answer as the page shows it, or `null` for a body this app cannot read (the caller treats that as
 * "service unavailable"). Tolerant on purpose: a `null` quote, a `null` page count, a warning code or a change type
 * it does not know never throws and never drops a change.
 */
export const normaliseCompareResponse = (raw: unknown): CompareResult | null => {
  if (!isObject(raw)) return null;
  const status = raw.status;
  if (!isCompareStatus(status)) return null;

  const summary = asString(raw.summary);
  const counts = toCounts(raw.counts);
  const original = toSide(raw.original);
  const revised = toSide(raw.revised);
  const warnings = toWarnings(raw.warnings);
  const changes =
    status === "changes_found" && Array.isArray(raw.changes)
      ? raw.changes.flatMap((change, index) => {
          const card = toCard(change, index);
          return card ? [card] : [];
        })
      : [];
  const counted = TYPE_ORDER.reduce((sum, type) => sum + counts[type], 0);
  const total = status === "changes_found" ? Math.max(counted, changes.length) : 0;

  const filters: CompareFilterChip[] =
    status === "changes_found"
      ? [
          { key: "all", label: copy.filterAll, count: total },
          ...TYPE_ORDER.filter((type) => counts[type] > 0).map((type) => ({
            key: type as CompareFilterKey,
            label: TYPE_LABELS[type],
            count: counts[type],
          })),
        ]
      : [];

  // Moves and blanks are always worded by code; only a changed / added / removed one means the model fell back.
  const sentencesByCode =
    warnings.some((warning) => warning.code === "ai_unavailable") ||
    changes.some(
      (change) =>
        change.byCode &&
        (change.type === "changed" ||
          change.type === "added" ||
          change.type === "removed"),
    );

  const base = {
    status,
    summary,
    reason: null as string | null,
    switchToUpload: false,
    counts,
    total,
    original,
    revised,
    changes,
    filters,
    notices: [] as CompareNotice[],
    sentencesByCode,
  };

  if (status === "changes_found") {
    return {
      ...base,
      kind: "changes",
      message: "",
      notices: buildNotices(warnings, changes.length, total),
    };
  }
  if (status === "no_changes") {
    return { ...base, kind: "noChanges", message: copy.noChanges };
  }
  if (status === "different_documents") {
    return {
      ...base,
      kind: "differentDocuments",
      message: copy.differentDocuments,
    };
  }
  const reason = asString(raw.reason);
  const told = describeNotCompared(reason, summary, original, warnings);
  return {
    ...base,
    kind: "notCompared",
    reason: reason || null,
    message: told.message || copy.unavailable,
    switchToUpload: told.switchToUpload,
  };
};

// -------------------------------------------------------------------------------------- the change list

export const filterChanges = (
  changes: ChangeCardModel[],
  key: CompareFilterKey,
): ChangeCardModel[] =>
  key === "all" ? changes : changes.filter((change) => change.type === key);

/** How many cards to show after one more "Show more" press. */
export const nextShownCount = (shown: number, available: number): number =>
  Math.min(available, shown + CARDS_PER_PAGE);

// ------------------------------------------------------------------------------------------- the failure

/** What the page knows about the call when it failed. */
export interface ErrorContext {
  /** The abort fired (`COMPARE_ABORT_MS`). */
  timedOut: boolean;
  /** What was sent as the old version: a pick sends one file (the new version), an upload two. */
  oldKind: OldVersion["kind"];
  /** Milliseconds since the user last cancelled a compare, or `null`. */
  sinceCancelMs: number | null;
}

/**
 * A body counts as JSON only when it is an object that `makeCall` threw as parsed: not an `Error` (a body that was
 * not JSON arrives as a `SyntaxError`, a dropped connection as a `TypeError`, an abort as a `DOMException`), not
 * `makeCall`'s own timer's `{error: true}`, and not the placeholder `makeCall` builds for an empty body.
 */
export const isJsonBody = (error: unknown): error is Body =>
  isObject(error) &&
  !(error instanceof Error) &&
  error.error !== true &&
  error.message !== notifiers.GENERIC_ERROR;

/** The server's own words -- only from a JSON body with a non-empty string `message`. */
const serverMessage = (error: unknown): string | null =>
  isJsonBody(error) && typeof error.message === "string" && error.message.trim()
    ? error.message
    : null;

const partOf = (error: unknown): ComparePart | null =>
  isJsonBody(error) && (COMPARE_PARTS as readonly unknown[]).includes(error.part)
    ? (error.part as ComparePart)
    : null;

/** A part the page actually sent -- an error naming another one is not about a file on screen. */
const wasSent = (part: ComparePart, context: ErrorContext): boolean =>
  part === "revised" ||
  (part === "original" && context.oldKind === "upload") ||
  (part === "originalDocumentId" && context.oldKind === "library");

const retryAfterOf = (error: unknown): number | null => {
  if (!isJsonBody(error)) return null;
  const seconds = Number(error.retryAfter);
  return Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : null;
};

/**
 * Makes sense of whatever the compare call threw. Placed by status, `part` and `reason` -- never by English text
 * (the plan-403 wording is the one exception, and it lives in `parsePlanForbidden`). Order: the timeout, then
 * the gate (a client block, core's plan 403, a 423), a permission block, 401, a network failure, and last the status.
 */
export const classifyError = (
  error: unknown,
  context: ErrorContext,
): CompareFailure => {
  const timedOut =
    context.timedOut || (isObject(error) && error.error === true);
  if (timedOut) return { kind: "timedOut", message: copy.tookTooLong };

  // Gate first. A FeatureBlockedError carries code 403 / 423 and the "not available on your plan" wording, so the two
  // parsers below see it as well.
  if (isFeatureBlockedError(error)) {
    const admin =
      (error as Body).featureBlockedStatus ===
      strings.FEATURE_STATUS_ADMIN_DISABLED;
    return {
      kind: "gate",
      gate: admin ? "admin" : "plan",
      message: admin ? serverMessage(error) : null,
    };
  }
  if (parsePlanForbidden(error).isPlanForbidden) {
    return { kind: "gate", gate: "plan", message: null };
  }
  if (parseAdminDisabled(error).isAdminDisabled) {
    return { kind: "gate", gate: "admin", message: serverMessage(error) };
  }
  // A permission block thrown before sending is not a plan 403: it goes to the Unauthorized page by itself.
  if (isPermissionBlockedError(error)) return { kind: "unauthorized" };

  const status = getApiStatus(error);
  if (status === apiStatus.unauthorized) return { kind: "session" };
  if (isNetworkError(error)) {
    return { kind: "unavailable", message: copy.unavailable };
  }

  const json = isJsonBody(error);
  const part = partOf(error);
  const unavailable: CompareFailure = {
    kind: "unavailable",
    message: copy.unavailable,
  };

  switch (status) {
    case 400: {
      const message = serverMessage(error) ?? copy.fileRefused;
      if (part && wasSent(part, context)) {
        return { kind: "file", part, message, dropPick: false };
      }
      return { kind: "general", message };
    }
    case 403:
      // A 403 that is not the plan's is a missing role -- but only a JSON body says so: a proxy or firewall page
      // is a service problem, not "you may not".
      return json ? { kind: "unauthorized" } : unavailable;
    case 404:
      // Only the AI's own "no such contract" (it names the part and an id was sent) reads as not found. Any other
      // 404 -- a route missing on a mis-ordered deploy -- is a service problem.
      return json &&
        part === "originalDocumentId" &&
        context.oldKind === "library"
        ? {
            kind: "file",
            part,
            message: serverMessage(error) ?? copy.contractNotFound,
            dropPick: true,
          }
        : unavailable;
    case 413: {
      // Always the page's own single-file words, never the server's ("'revised' must be 10 MB or smaller.").
      if (part && wasSent(part, context)) {
        return { kind: "file", part, message: copy.fileTooBig, dropPick: false };
      }
      // Two files and no part: the request as a whole was refused outside the page's own checks (a proxy, a cap).
      if (context.oldKind === "upload") {
        return { kind: "general", message: copy.filesTooBig };
      }
      // One file was sent: it is the one that is too big.
      return {
        kind: "file",
        part: "revised",
        message: copy.fileTooBig,
        dropPick: false,
      };
    }
    case 415:
      return { kind: "reload", message: copy.notSent };
    case 429: {
      const seconds = retryAfterOf(error) ?? DEFAULT_RETRY_AFTER_SECONDS;
      if (seconds > LIMIT_FRAME_THRESHOLD_SECONDS) {
        return {
          kind: "limit",
          message: serverMessage(error) ?? copy.limitReached,
        };
      }
      const stillFinishing =
        context.sinceCancelMs !== null &&
        context.sinceCancelMs >= 0 &&
        context.sinceCancelMs <= CANCEL_GRACE_MS;
      return { kind: "busy", retryAfterSeconds: seconds, stillFinishing };
    }
    case 503:
      // Only the AI's own switch-off says so (H4). A busy core is a 503 too, and reading it as "off" would mislead.
      if (json && (error as Body).reason === "switched_off") {
        return { kind: "switchedOff", message: copy.switchedOff };
      }
      return {
        kind: "busy",
        retryAfterSeconds: retryAfterOf(error) ?? DEFAULT_RETRY_AFTER_SECONDS,
        stillFinishing: false,
      };
    default:
      // 500, 502, 504, any other status, and an error with none.
      return unavailable;
  }
};

/** For the "failed" analytics event: `http_<status>`, `timeout` or `network` -- never any text of the error. */
export const failureReason = (
  error: unknown,
  context: Pick<ErrorContext, "timedOut">,
): string => {
  if (context.timedOut || (isObject(error) && error.error === true)) {
    return "timeout";
  }
  const status = getApiStatus(error);
  return status !== undefined ? `http_${status}` : "network";
};


// ------------------------------------------------------------------------------------------ the picker's list

/** The file kind of a name, for the tile: a `.docx` is Word, anything else the list lets through is a PDF. */
export const kindOfFileName = (fileName: string): "pdf" | "docx" =>
  /\.docx$/i.test(fileName.trim()) ? "docx" : "pdf";

/** "1 page" / "6 pages", or an empty string when the count is not known. */
export const pageCountLabel = (pages: number | null | undefined): string => {
  const page = copy.page;
  if (typeof pages !== "number" || !Number.isFinite(pages) || pages < 1) return "";
  return pages === 1 ? page.metaPageOne : fillCopy(page.metaPageMany, { count: pages });
};

/** The row's detail line: "Signed 14 Oct 2026, 16:20 · 6 pages", or "Uploaded 9 Oct 2026, 11:42". */
export const libraryRowMeta = (document: ContractDocument): string => {
  const page = copy.page;
  const signedAt = document.signed ? document.signatureDetails?.signedAt : null;
  const signedWhen = formatShortDate(signedAt, true);
  const uploadedWhen = formatShortDate(document.createdAt, true);
  const lead = signedWhen
    ? fillCopy(page.metaSigned, { when: signedWhen })
    : uploadedWhen
      ? fillCopy(page.metaUploaded, { when: uploadedWhen })
      : "";
  return [lead, pageCountLabel(document.pageCount)].filter(Boolean).join(page.separator);
};

/**
 * The contracts the picker lists: the user's own PDF and Word rows (told apart by the extension, any case), newest
 * first. No filter on status or visibility: a row that is still processing has a stored file too (D46).
 */
export const pickableContracts = (documents: ContractDocument[]): ContractDocument[] =>
  documents
    .filter((document) => hasAllowedExtension(document.filename ?? ""))
    .sort((a, b) => {
      const left = Date.parse(a.createdAt);
      const right = Date.parse(b.createdAt);
      return (Number.isNaN(right) ? 0 : right) - (Number.isNaN(left) ? 0 : left);
    });

/** The search box filters in the browser, by file name, ignoring case and surrounding spaces. */
export const filterContracts = (
  documents: ContractDocument[],
  query: string,
): ContractDocument[] => {
  const needle = query.trim().toLowerCase();
  if (!needle) return documents;
  return documents.filter((document) =>
    (document.filename ?? "").toLowerCase().includes(needle),
  );
};
